import { parsePhoneNumber, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

// UK + USA + EU/EEA (Eurozone + non-eurozone EU + EEA non-EU)
export const COUNTRIES = [
  { code: 'IE', name: 'Ireland',        flag: '🇮🇪', dial: '+353' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dial: '+44'  },
  { code: 'US', name: 'United States',  flag: '🇺🇸', dial: '+1'   },
  // Eurozone EU
  { code: 'AT', name: 'Austria',        flag: '🇦🇹', dial: '+43'  },
  { code: 'BE', name: 'Belgium',        flag: '🇧🇪', dial: '+32'  },
  { code: 'CY', name: 'Cyprus',         flag: '🇨🇾', dial: '+357' },
  { code: 'EE', name: 'Estonia',        flag: '🇪🇪', dial: '+372' },
  { code: 'FI', name: 'Finland',        flag: '🇫🇮', dial: '+358' },
  { code: 'FR', name: 'France',         flag: '🇫🇷', dial: '+33'  },
  { code: 'DE', name: 'Germany',        flag: '🇩🇪', dial: '+49'  },
  { code: 'GR', name: 'Greece',         flag: '🇬🇷', dial: '+30'  },
  { code: 'HR', name: 'Croatia',        flag: '🇭🇷', dial: '+385' },
  { code: 'IT', name: 'Italy',          flag: '🇮🇹', dial: '+39'  },
  { code: 'LV', name: 'Latvia',         flag: '🇱🇻', dial: '+371' },
  { code: 'LT', name: 'Lithuania',      flag: '🇱🇹', dial: '+370' },
  { code: 'LU', name: 'Luxembourg',     flag: '🇱🇺', dial: '+352' },
  { code: 'MT', name: 'Malta',          flag: '🇲🇹', dial: '+356' },
  { code: 'NL', name: 'Netherlands',    flag: '🇳🇱', dial: '+31'  },
  { code: 'PT', name: 'Portugal',       flag: '🇵🇹', dial: '+351' },
  { code: 'SK', name: 'Slovakia',       flag: '🇸🇰', dial: '+421' },
  { code: 'SI', name: 'Slovenia',       flag: '🇸🇮', dial: '+386' },
  { code: 'ES', name: 'Spain',          flag: '🇪🇸', dial: '+34'  },
  // Non-eurozone EU (operates in EUR — bank converts on receipt)
  { code: 'BG', name: 'Bulgaria',       flag: '🇧🇬', dial: '+359' },
  { code: 'CZ', name: 'Czechia',        flag: '🇨🇿', dial: '+420' },
  { code: 'DK', name: 'Denmark',        flag: '🇩🇰', dial: '+45'  },
  { code: 'HU', name: 'Hungary',        flag: '🇭🇺', dial: '+36'  },
  { code: 'PL', name: 'Poland',         flag: '🇵🇱', dial: '+48'  },
  { code: 'RO', name: 'Romania',        flag: '🇷🇴', dial: '+40'  },
  { code: 'SE', name: 'Sweden',         flag: '🇸🇪', dial: '+46'  },
  // EEA non-EU (also in SEPA — operates in EUR)
  { code: 'IS', name: 'Iceland',        flag: '🇮🇸', dial: '+354' },
  { code: 'NO', name: 'Norway',         flag: '🇳🇴', dial: '+47'  },
  // Mobile numbers staff actually carry into US/UK/IE venues. The number's country
  // is only the picker's default for where they live; About you asks that separately.
  { code: 'CH', name: 'Switzerland',    flag: '🇨🇭', dial: '+41'  },
  { code: 'UA', name: 'Ukraine',        flag: '🇺🇦', dial: '+380' },
  { code: 'TR', name: 'Türkiye',        flag: '🇹🇷', dial: '+90'  },
  { code: 'CA', name: 'Canada',         flag: '🇨🇦', dial: '+1'   },
  { code: 'MX', name: 'Mexico',         flag: '🇲🇽', dial: '+52'  },
  { code: 'BR', name: 'Brazil',         flag: '🇧🇷', dial: '+55'  },
  { code: 'AR', name: 'Argentina',      flag: '🇦🇷', dial: '+54'  },
  { code: 'CO', name: 'Colombia',       flag: '🇨🇴', dial: '+57'  },
  { code: 'IN', name: 'India',          flag: '🇮🇳', dial: '+91'  },
  { code: 'PK', name: 'Pakistan',       flag: '🇵🇰', dial: '+92'  },
  { code: 'PH', name: 'Philippines',    flag: '🇵🇭', dial: '+63'  },
  { code: 'NG', name: 'Nigeria',        flag: '🇳🇬', dial: '+234' },
  { code: 'ZA', name: 'South Africa',   flag: '🇿🇦', dial: '+27'  },
  { code: 'AU', name: 'Australia',      flag: '🇦🇺', dial: '+61'  },
  { code: 'NZ', name: 'New Zealand',    flag: '🇳🇿', dial: '+64'  },
];

export function getPlaceholder(countryCode) {
  try {
    const example = getExampleNumber(countryCode, examples);
    return example ? example.formatNational() : '';
  } catch {
    return '';
  }
}

/**
 * Returns an E.164 string for valid input, otherwise null. Never throws.
 *
 * Gates on isValid() (strict national numbering-plan match), which catches typos —
 * e.g. a US exchange code starting with 0/1 is rejected. If real, freshly-assigned
 * numbers ever get wrongly rejected because libphonenumber's metadata lags, relax
 * this to isPossible() (length-only); OTP delivery is the ultimate validator.
 */
export function parseE164(rawInput, countryCode) {
  if (!rawInput) return null;
  try {
    const parsed = parsePhoneNumber(rawInput, countryCode);
    if (parsed && parsed.isValid()) return parsed.format('E.164');
  } catch { /* continue */ }
  return null;
}

/**
 * Formats an E.164 (or any +… number) for display in international style, e.g.
 * "+353 87 200 4000". Non-phone or unparseable input (including emails) is returned
 * unchanged so it's safe to call on a mixed identifier. Never throws.
 */
export function formatE164ForDisplay(value) {
  if (!value || typeof value !== 'string' || !value.startsWith('+')) return value;
  try {
    const parsed = parsePhoneNumber(value);
    if (parsed) return parsed.formatInternational();
  } catch { /* fall through */ }
  return value;
}

/**
 * Privacy-preserving partial mask of a phone number for logging/analytics: the
 * national number's first 3 and last 2 digits with a fixed masked middle — enough to
 * see the area code + a little uniqueness without storing the full number.
 * e.g. "+1 (650) 555-1234" → "650***34". Returns null if unparseable. Never throws.
 */
export function maskPhoneNumber(e164) {
  if (!e164 || typeof e164 !== 'string') return null;
  try {
    const parsed = parsePhoneNumber(e164);
    const national = parsed && parsed.nationalNumber ? String(parsed.nationalNumber) : null;
    if (!national) return null;
    if (national.length <= 5) return `${national.slice(0, 1)}***${national.slice(-1)}`;
    return `${national.slice(0, 3)}***${national.slice(-2)}`;
  } catch {
    return null;
  }
}

/**
 * Region of the browser locale ("en-US" → "US"), or null when it carries none.
 * A heuristic, not geolocation: it reflects the device's language setting.
 */
export function browserRegion() {
  try {
    const lang = typeof navigator !== 'undefined' ? navigator.language : null;
    if (!lang) return null;
    const match = String(lang).match(/[-_]([A-Za-z]{2})(?:[-_]|$)/);
    return match ? match[1].toUpperCase() : null;
  } catch {
    return null;
  }
}

// Locale regions that don't mean what they say. Irish phones very commonly run
// en-GB (there is an en-IE, but it is not what most devices end up on), so a GB
// region is as likely Irish as British — and Ireland is the home market. Map it
// to IE: Irish staff keep +353 and UK staff are no worse off than before (they
// always opened on +353). en-US → US is unambiguous and is the case that hurt.
const AMBIGUOUS_REGIONS = { GB: 'IE' };

// The venue's currency is the best signed-out signal we have for where its staff
// are: a tag/team paying out in USD is in the US whatever the device says. EUR is
// read as Ireland (home market; the eurozone ambiguity is the same as today's
// default). Anything else gives no hint.
const DEFAULT_COUNTRY_BY_CURRENCY = { EUR: 'IE', GBP: 'GB', USD: 'US' };

/** The country a currency implies for a phone picker, or null. */
export function countryForCurrency(currency) {
  return (currency && DEFAULT_COUNTRY_BY_CURRENCY[String(currency).toUpperCase()]) || null;
}

/**
 * Which country a phone picker should open on.
 *
 * COUNTRIES is Ireland-first, and every picker used to open on that first entry
 * regardless of who was looking at it — a US staff member joining a team landed
 * on +353. Prefer the person's profile country when the caller knows it, then the
 * browser locale's region (with the ambiguity above resolved), then the first
 * list entry. Only countries we support are honoured; anything else falls
 * through to the next source. Not geolocation: a device with no region in its
 * locale lands on the first entry (Ireland).
 */
export function defaultCountry(profileCountry) {
  const byCode = (code) => (code
    ? COUNTRIES.find((c) => c.code === String(code).toUpperCase())
    : null);
  const region = browserRegion();
  const localeCountry = (region && AMBIGUOUS_REGIONS[region]) || region;
  return byCode(profileCountry) || byCode(localeCountry) || COUNTRIES[0];
}

/**
 * Splits an E.164 number back into { country, national } so a phone input can be
 * pre-filled (e.g. carrying the number typed on the identity-first screen).
 * Returns null if it can't be parsed. Never throws.
 */
export function nationalFromE164(e164) {
  if (!e164) return null;
  try {
    const parsed = parsePhoneNumber(e164);
    if (parsed && parsed.country) {
      return { country: parsed.country, national: parsed.formatNational() };
    }
  } catch { /* continue */ }
  return null;
}
